import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import WishlistCard from './components/WishlistCard';
import WishlistFilters from './components/WishlistFilters';
import CreateWishlistModal from './components/CreateWishlistModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { wishlistsService } from '../../services/wishlists';

const WishlistsPage = () => {
  const [wishlists, setWishlists] = useState([]);
  const [filteredWishlists, setFilteredWishlists] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'recent',
    totalCount: 0
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Load wishlists from API
  useEffect(() => {
    const loadWishlists = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wishlistsService.getWishlists();
        setWishlists(response);
        setFilters(prev => ({ ...prev, totalCount: response.length }));
      } catch (err) {
        console.error('Error loading wishlists:', err);
        setError('Failed to load wishlists. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadWishlists();
    } else {
      // User is not authenticated, stop loading and show login prompt
      setLoading(false);
      setError('Please log in to view your wishlists.');
    }
  }, [user]);

  useEffect(() => {
    let filtered = [...wishlists];

    // Apply search filter
    if (filters.search) {
      filtered = filtered.filter(wishlist =>
        wishlist.name.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'items':
        filtered.sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
        break;
      default: // recent
        filtered.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        break;
    }

    setFilteredWishlists(filtered);
  }, [wishlists, filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleCreateWishlist = async (wishlistData) => {
    try {
      const newWishlist = await wishlistsService.createWishlist(wishlistData);
      setWishlists(prev => [newWishlist, ...prev]);
      setFilters(prev => ({ ...prev, totalCount: prev.totalCount + 1 }));
      return newWishlist;
    } catch (err) {
      console.error('Error creating wishlist:', err);
      throw err;
    }
  };

  const handleEditWishlist = async (wishlistId, updates) => {
    try {
      const updatedWishlist = await wishlistsService.updateWishlist(wishlistId, updates);
      setWishlists(prev => prev.map(w => w.id === wishlistId ? updatedWishlist : w));
    } catch (err) {
      console.error('Error updating wishlist:', err);
      throw err;
    }
  };

  const handleDeleteWishlist = async (wishlistId) => {
    try {
      await wishlistsService.deleteWishlist(wishlistId);
      setWishlists(prev => prev.filter(w => w.id !== wishlistId));
      setFilters(prev => ({ ...prev, totalCount: prev.totalCount - 1 }));
    } catch (err) {
      console.error('Error deleting wishlist:', err);
      throw err;
    }
  };

  const handleShareWishlist = (wishlistId) => {
    console.log('Share wishlist:', wishlistId);
    // Implementation for sharing wishlist
  };

  const handleDuplicateWishlist = async (wishlistId) => {
    try {
      const originalWishlist = wishlists.find(w => w.id === wishlistId);
      if (originalWishlist) {
        const duplicatedWishlist = await wishlistsService.createWishlist({
          name: `${originalWishlist.name} (Copy)`,
          description: originalWishlist.description
        });
        setWishlists(prev => [duplicatedWishlist, ...prev]);
        setFilters(prev => ({ ...prev, totalCount: prev.totalCount + 1 }));
      }
    } catch (err) {
      console.error('Error duplicating wishlist:', err);
      throw err;
    }
  };

  // Transform wishlist data for display
  const transformWishlistForCard = (wishlist) => ({
    id: wishlist.id,
    name: wishlist.name,
    description: wishlist.description || '',
    itemCount: wishlist.product_count,
    lastUpdated: formatDate(wishlist.updated_at),
    coverImage: wishlist.items && wishlist.items.length > 0
      ? wishlist.items[0].product?.image_url || '/images/default-wishlist.jpg'
      : '/images/default-wishlist.jpg',
    privacy: 'private', // Default since Django API doesn't include privacy yet
    occasion: null,
    views: 0, // Not available in current API
    completionRate: 0, // Not available in current API
    priorities: {
      mustHave: 0,
      wouldLove: 0,
      justIdeas: 0
    },
    collaborators: []
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Heart" size={32} className="text-primary animate-pulse" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                  Loading your wishlists...
                </h3>
                <p className="text-accent-600">
                  Please wait while we fetch your gift collections
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('log in') || error.includes('login') || error.includes('authenticate');

    return (
      <div className="min-h-screen bg-gradient-warm">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isAuthError ? 'bg-primary-100' : 'bg-error-100'
                  }`}>
                  <Icon
                    name={isAuthError ? "LogIn" : "AlertCircle"}
                    size={32}
                    className={isAuthError ? "text-primary" : "text-error-600"}
                  />
                </div>
                <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                  {isAuthError ? 'Sign in to view your wishlists' : 'Unable to load wishlists'}
                </h3>
                <p className="text-accent-600 mb-6">
                  {isAuthError
                    ? 'Create an account or sign in to start building your perfect gift collections.'
                    : error
                  }
                </p>
                {isAuthError ? (
                  <div className="space-y-3">
                    <Button
                      variant="primary"
                      onClick={() => window.location.href = '/login'}
                      iconName="LogIn"
                      iconPosition="left"
                    >
                      Sign In
                    </Button>
                    <div className="text-sm text-accent-600">
                      Don't have an account?{' '}
                      <button
                        onClick={() => window.location.href = '/register'}
                        className="text-primary hover:text-primary-600 font-medium"
                      >
                        Create one now
                      </button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => window.location.reload()}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm">
      <Header />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-2xl flex items-center justify-center shadow-warm">
                <Icon name="Heart" size={24} color="white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800">
                  My Wishlists
                </h1>
                <p className="text-accent-600 mt-1">
                  Curate, organize, and share your perfect gift collections
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <WishlistFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onCreateNew={() => setIsCreateModalOpen(true)}
          />

          {/* Analytics placeholder for future implementation */}

          {/* View Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-accent-600">
                {filteredWishlists.length} of {wishlists.length} wishlists
              </span>
              {filteredWishlists.length !== wishlists.length && (
                <button
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    search: ''
                  }))}
                  className="text-sm text-primary hover:text-primary-600 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-accent-600 hover:bg-surface'
                  }`}
              >
                <Icon name="Grid3X3" size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-accent-600 hover:bg-surface'
                  }`}
              >
                <Icon name="List" size={18} />
              </button>
            </div>
          </div>

          {/* Wishlists Grid */}
          {filteredWishlists.length > 0 ? (
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
              }`}>
              {filteredWishlists.map((wishlist) => (
                <WishlistCard
                  key={wishlist.id}
                  wishlist={transformWishlistForCard(wishlist)}
                  onEdit={(updates) => handleEditWishlist(wishlist.id, updates)}
                  onDelete={() => handleDeleteWishlist(wishlist.id)}
                  onDuplicate={() => handleDuplicateWishlist(wishlist.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Heart" size={32} className="text-accent-400" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                {filters.search ? 'No wishlists match your search' : 'Create your first wishlist'}
              </h3>
              <p className="text-accent-600 mb-6 max-w-md mx-auto">
                {filters.search ? 'Try adjusting your search criteria to see more results.' : 'Start curating your perfect gift collection and share it with friends and family.'}
              </p>
              <Button
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                className="shadow-warm"
              >
                Create Your First Wishlist
              </Button>
            </div>
          )}

          {/* Quick Actions */}
          {filteredWishlists.length > 0 && (
            <div className="mt-12 bg-white rounded-2xl shadow-md p-6">
              <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button className="flex items-center space-x-3 p-4 bg-surface rounded-xl hover:bg-accent-50 transition-colors text-left">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Icon name="Share" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-accent-800">Share All Lists</div>
                    <div className="text-sm text-accent-600">Send your wishlists to family</div>
                  </div>
                </button>

                <button className="flex items-center space-x-3 p-4 bg-surface rounded-xl hover:bg-accent-50 transition-colors text-left">
                  <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                    <Icon name="Download" size={20} className="text-secondary-600" />
                  </div>
                  <div>
                    <div className="font-medium text-accent-800">Export Lists</div>
                    <div className="text-sm text-accent-600">Download as PDF or CSV</div>
                  </div>
                </button>

                <button className="flex items-center space-x-3 p-4 bg-surface rounded-xl hover:bg-accent-50 transition-colors text-left">
                  <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                    <Icon name="Settings" size={20} className="text-success-600" />
                  </div>
                  <div>
                    <div className="font-medium text-accent-800">Manage Settings</div>
                    <div className="text-sm text-accent-600">Privacy and notifications</div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create Wishlist Modal */}
      <CreateWishlistModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWishlist={handleCreateWishlist}
      />
    </div>
  );
};

export default WishlistsPage;